import React, { useState } from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { toast } from 'react-hot-toast';

const ShareSurveyModal = ({ isOpen, onClose, survey, businessId, shareUrl, expiresAt, loading = false, onRegenerate }) => {
    if (!isOpen || !survey) return null;

    const frontendBaseURL = window.location.origin;
    const effectiveShareUrl = shareUrl || '';

    const handleCopyLink = () => {
        if (!effectiveShareUrl) {
            toast.error('Secure link not ready yet.');
            return;
        }
        navigator.clipboard.writeText(effectiveShareUrl).then(() => {
            toast.success('Survey link copied to clipboard!');
        }, () => {
            toast.error('Failed to copy link.');
        });
    };

    const downloadQRCode = () => {
        const svgElement = document.getElementById('share-qr-code');
        if (!svgElement) {
            toast.error('QR code not found.');
            return;
        }

        try {
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            // Add some margin and size
            const size = 256;
            canvas.width = size;
            canvas.height = size;

            img.onload = () => {
                // Fill white background
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                // Draw image centered
                ctx.drawImage(img, 0, 0, size, size);

                const link = document.createElement('a');
                link.download = `survey-${survey.id}-qr.png`;
                link.href = canvas.toDataURL('image/png');
                link.click();
                
                toast.success('QR Code downloaded!');
            };

            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            img.src = url;

        } catch (error) {
            console.error('QR download error:', error);
            toast.error('Failed to download QR code.');
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                <div className="modal-header">
                    <h2 className="new-chat-title" style={{ color: 'white' }}>
                        <i className="ri-share-line" style={{ marginRight: '8px' }}></i>
                        Share Survey
                    </h2>
                    <button className="close-button" onClick={onClose}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>
                
                <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: '20px' }}>
                         <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>
                            {survey.title}
                        </h3>
                        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                            Share this survey with your audience.
                        </p>
                    </div>

                    {/* Link Section */}
                    <div style={{ marginBottom: '25px' }}>
                        <label style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: '#444' }}>
                            Survey link (title is visible to participants)
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input 
                                type="text" 
                                value={
                                    loading
                                        ? 'Generating link...'
                                        : effectiveShareUrl || 'Link unavailable. Publish or regenerate to create it.'
                                } 
                                readOnly 
                                style={{ 
                                    flex: 1, 
                                    padding: '10px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '6px',
                                    color: '#555',
                                    backgroundColor: '#f8f9fa'
                                }} 
                            />
                            <button 
                                onClick={handleCopyLink}
                                disabled={loading || !effectiveShareUrl}
                                style={{
                                    padding: '0 16px',
                                    backgroundColor: loading ? '#9e9e9e' : '#4caf50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                title="Copy Link"
                            >
                                <i className="ri-file-copy-line"></i>
                            </button>
                            {onRegenerate && (
                                <button
                                    onClick={onRegenerate}
                                    disabled={loading}
                                    style={{
                                        padding: '0 12px',
                                        backgroundColor: '#2196f3',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    title="Regenerate link"
                                >
                                    <i className="ri-refresh-line"></i>
                                </button>
                            )}
                        </div>
                        <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                            This link and its QR stay active until you unpublish the survey.
                        </p>
                    </div>

                    {/* QR Code Section */}
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '20px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        border: '1px solid #eee'
                    }}>
                        <div style={{ marginBottom: '16px', background: 'white', padding: '10px', display: 'inline-block', borderRadius: '8px' }}>
                            {loading || !effectiveShareUrl ? (
                                <div style={{ padding: '30px', color: '#666' }}>
                                    {loading ? 'Generating QR...' : 'Link not ready. Publish or regenerate to create it.'}
                                </div>
                            ) : (
                                <QRCode 
                                    id="share-qr-code"
                                    value={effectiveShareUrl}
                                    size={180}
                                    level="H"
                                    includeMargin={true}
                                />
                            )}
                        </div>
                        <div>
                            <button 
                                onClick={downloadQRCode}
                                className="dashboard-button"
                                style={{ margin: '0 auto' }}
                                disabled={loading || !effectiveShareUrl}
                            >
                                <i className="ri-download-line"></i>
                                Download QR Code
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShareSurveyModal;
